// src/modules/units/tyres/views/tyre-catalogue.api.js
//
// Tyre catalogue client. Catalogue rows are scoped by the backend to the ACTIVE
// customer. The management list includes inactive rows; /choices is the smaller
// active-only list used by the tyre record form's typeaheads.

import { getUserData } from '../../../auth/views/login/auth.api.js';
import { emitTyreEvent, TYRE_EVENTS } from '../shared/tyre-events.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';

async function request(path, options = {}) {
  const { token } = getUserData() || {};

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    credentials: 'include'
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return {
      success: false,
      status: response.status,
      message: data?.message || data?.error || 'Tyre catalogue request failed.',
      data
    };
  }

  return { success: true, status: response.status, data };
}

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

function catalogueQuery({ sort = 'manufacturerName,modelName', limit, offset = 0 } = {}) {
  const q = new URLSearchParams();
  if (sort) q.set('sort', sort);
  if (Number(limit) > 0) q.set('limit', String(limit));
  if (Number(offset) >= 0) q.set('offset', String(offset));
  return q;
}

/** Return all catalogue rows for the active customer unless a positive limit is
 * explicitly requested. Paging here avoids the endpoint's default 20-row cap. */
export async function fetchTyreCatalogue(opts = {}, { signal } = {}) {
  if (Number(opts.limit) > 0) {
    return request(`/tyre-catalogue?${catalogueQuery(opts).toString()}`, { method: 'GET', signal });
  }

  const rows = [];
  let count = NaN;
  let status = 200;

  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await request(
      `/tyre-catalogue?${catalogueQuery({ ...opts, limit: PAGE_SIZE, offset: rows.length }).toString()}`,
      { method: 'GET', signal }
    );
    if (!res.success) return res;

    status = res.status;
    const batch = Array.isArray(res.data?.results) ? res.data.results : [];
    count = Number(res.data?.count);
    rows.push(...batch);

    if (batch.length < PAGE_SIZE) break;
    if (Number.isFinite(count) && rows.length >= count) break;
  }

  rows.sort((a, b) => {
    const ma = String(a?.manufacturerName ?? '');
    const mb = String(b?.manufacturerName ?? '');
    const m = ma.localeCompare(mb, undefined, { numeric: true, sensitivity: 'base' });
    if (m) return m;
    return String(a?.modelName ?? '').localeCompare(
      String(b?.modelName ?? ''), undefined, { numeric: true, sensitivity: 'base' }
    );
  });

  return {
    success: true,
    status,
    data: {
      count: Number.isFinite(count) ? count : rows.length,
      results: rows
    }
  };
}

export async function fetchTyreCatalogueEntry(tyreCatalogueEntryId) {
  return request(`/tyre-catalogue/${encodeURIComponent(tyreCatalogueEntryId)}`, { method: 'GET' });
}

export async function fetchTyreCatalogueChoices({ signal } = {}) {
  return request('/tyre-catalogue/choices', { method: 'GET', signal });
}

function catalogueBody(payload = {}) {
  const body = {
    manufacturerName: String(payload.manufacturerName || '').trim(),
    modelName: String(payload.modelName || '').trim(),
    treadPatternCode: String(payload.treadPatternCode || '').trim(),
    treadPatternDescription: String(payload.treadPatternDescription || '').trim(),
    tyreSize: String(payload.tyreSize || '').trim(),
    tyreApplication: String(payload.tyreApplication || '').trim(),
    tyreType: String(payload.tyreType || '').trim(),
    tyreGrade: String(payload.tyreGrade || '').trim(),
    isActive: payload.isActive !== false
  };

  [
    'manufacturerNominalTreadDepth',
    'manufacturerExpectedMileage',
    'minimumTreadForRetread'
  ].forEach((key) => {
    if (payload[key] !== '' && payload[key] !== null && payload[key] !== undefined) {
      body[key] = Number(payload[key]);
    }
  });

  return body;
}

export async function createTyreCatalogueEntry(payload = {}) {
  const res = await request('/tyre-catalogue', {
    method: 'POST',
    body: JSON.stringify(catalogueBody(payload))
  });

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.CATALOGUE_CHANGED, {
      reason: 'create',
      tyreCatalogueEntryId: res.data?.tyreCatalogueEntryId
    });
  }

  return res;
}

export async function updateTyreCatalogueEntry(tyreCatalogueEntryId, payload = {}) {
  const res = await request(`/tyre-catalogue/${encodeURIComponent(tyreCatalogueEntryId)}`, {
    method: 'PATCH',
    body: JSON.stringify(catalogueBody(payload))
  });

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.CATALOGUE_CHANGED, {
      reason: 'update',
      tyreCatalogueEntryId
    });
  }

  return res;
}

export async function deleteTyreCatalogueEntry(tyreCatalogueEntryId) {
  const res = await request(`/tyre-catalogue/${encodeURIComponent(tyreCatalogueEntryId)}`, {
    method: 'DELETE'
  });

  if (res.success) {
    emitTyreEvent(TYRE_EVENTS.CATALOGUE_CHANGED, {
      reason: 'delete',
      tyreCatalogueEntryId
    });
  }

  return res;
}
